import React, { useState } from 'react';
import { push, ref, set, update } from 'firebase/database';
import { database } from '../firebaseConfig';
import { saveMarketingLink } from '../services/marketingLinks';

// Feature cards workflow, all in Resi:
// 1. Marketing manager pastes the link on "Create Feature Cards" and presses Send. That task is done and
//    "Review and Approve Feature Cards" appears for the agent.
// 2. Agent opens the link, picks a pickup option, and presses Approve. That task is done and
//    "Print and Produce Feature Cards" appears for the local assistant, with the link and pickup option.

export const PICKUP_OPTIONS = {
  corkscrew: 'Pick up from the local assistant at Corkscrew Shores',
  office: 'Pick up at the next team meeting at the office',
};

const nowIso = () => new Date().toISOString();
const todayDate = () => new Date().toISOString().slice(0, 10);

const newTask = (name, role, extra = {}) => ({
  name,
  role,
  type: 'Task',
  appliesTo: 'All',
  anchor: null,
  offsetDays: 0,
  shared: false,
  action: null,
  mode: 'task',
  dueDate: todayDate(),
  status: 'pending',
  completedBy: null,
  completedAt: null,
  createdBy: null,
  createdAt: nowIso(),
  ...extra,
});

const isCreate = (name) => /^create feature cards$/i.test((name || '').trim());
const isReview = (name) => /^review and approve feature cards$/i.test((name || '').trim());
const isPrint = (name) => /^print and produce feature cards$/i.test((name || '').trim());

export const isFeatureCardsTask = (name) => isCreate(name) || isReview(name) || isPrint(name);

const btn = { display: 'inline-block', padding: '8px 14px', borderRadius: 6, fontWeight: 700, fontSize: 13, textDecoration: 'none', border: '1px solid #B8962E', background: '#B8962E', color: '#FFFFFF', cursor: 'pointer' };
const btnOff = { ...btn, background: '#FFFFFF', color: '#B8962E', borderColor: '#D5D9E0', opacity: 0.5, cursor: 'not-allowed' };
const input = { flex: '1 1 260px', padding: '8px 10px', borderRadius: 6, border: '1px solid #D5D9E0', fontSize: 13, minWidth: 200 };

export default function FeatureCardsFlow({ task, txId }) {
  const taskName = task.name || task.title || '';
  const [link, setLink] = useState(task.featureCardsLink || '');
  const [pickup, setPickup] = useState(task.pickup || '');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  if (!isFeatureCardsTask(taskName) || !txId) return null;

  const taskPath = `transactions/${txId}/tasks/${task.id}`;

  // Marketing manager: save the link, finish this task, and create the agent's review task.
  const sendForReview = async () => {
    const value = link.trim();
    if (!/^https?:\/\//i.test(value)) { setError('Paste the full link, starting with https://'); return; }
    setBusy(true); setError('');
    try {
      await update(ref(database, taskPath), { featureCardsLink: value, status: 'completed', completedAt: nowIso() });
      await saveMarketingLink(txId, 'featureCards', 'Feature cards', value);
      await set(push(ref(database, `transactions/${txId}/tasks`)), newTask('Review and Approve Feature Cards', 'Agent', { featureCardsLink: value, createdBy: task.createdBy || null }));
    } catch (err) {
      console.error('Could not send feature cards for review', err);
      setError('Could not send. Please try again.');
    } finally { setBusy(false); }
  };

  // Agent: choose pickup, finish this task, and create the local assistant's print task.
  const approve = async () => {
    if (!pickup) { setError('Choose a pickup option first.'); return; }
    setBusy(true); setError('');
    try {
      await update(ref(database, taskPath), { pickup, status: 'completed', completedAt: nowIso() });
      await set(push(ref(database, `transactions/${txId}/tasks`)), newTask('Print and Produce Feature Cards', 'Local Assistant', {
        featureCardsLink: task.featureCardsLink || '',
        pickup,
        pickupLabel: PICKUP_OPTIONS[pickup],
      }));
    } catch (err) {
      console.error('Could not approve feature cards', err);
      setError('Could not approve. Please try again.');
    } finally { setBusy(false); }
  };

  if (isCreate(taskName)) {
    const ok = /^https?:\/\//i.test(link.trim());
    return (
      <div style={{ margin: '6px 0' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
          <input value={link} onChange={(e) => setLink(e.target.value)} placeholder="Paste the feature cards link (https://...)" style={input} />
          <button type="button" onClick={sendForReview} disabled={!ok || busy} style={ok && !busy ? btn : btnOff}>Send to agent for review</button>
        </div>
        {error && <div style={{ color: '#b00020', fontSize: 12, marginTop: 4 }}>{error}</div>}
      </div>
    );
  }

  if (isReview(taskName)) {
    const linkToOpen = task.featureCardsLink || '';
    return (
      <div style={{ margin: '6px 0' }}>
        {linkToOpen && <div style={{ fontSize: 13, marginBottom: 6 }}>Feature cards: <a href={linkToOpen} target="_blank" rel="noreferrer" style={{ color: '#8C6D1F', fontWeight: 600 }}>Open to review</a></div>}
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Pick up option (required)</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
          {Object.entries(PICKUP_OPTIONS).map(([value, label]) => (
            <label key={value} style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="radio" name={`pickup-${task.id}`} checked={pickup === value} onChange={() => setPickup(value)} />
              {label}
            </label>
          ))}
        </div>
        <button type="button" onClick={approve} disabled={!pickup || busy} style={pickup && !busy ? btn : btnOff}>Approve for printing</button>
        {error && <div style={{ color: '#b00020', fontSize: 12, marginTop: 4 }}>{error}</div>}
      </div>
    );
  }

  // Local assistant: the link and pickup option to print and produce the cards.
  return (
    <div style={{ margin: '6px 0', fontSize: 13 }}>
      {task.featureCardsLink && <div><a href={task.featureCardsLink} target="_blank" rel="noreferrer" style={{ color: '#8C6D1F', fontWeight: 600 }}>Open feature cards to print</a></div>}
      {task.pickupLabel && <div style={{ marginTop: 4 }}>{task.pickupLabel}</div>}
    </div>
  );
}
